import { useEffect, useState } from 'react';
import {
  Box, HStack, Text,
} from '@gluestack-ui/themed';

import { OrbLabScreen } from '@/visual-lab/chapters/orb/OrbLabScreen';
import { PatrolLabHeaderControls } from '@/visual-lab/chapters/patrol/PatrolLabHeaderControls';
import { PatrolLabScreen } from '@/visual-lab/chapters/patrol/PatrolLabScreen';
import { parsePatrolLabQuery } from '@/visual-lab/chapters/patrol/parsePatrolLabQuery';
import { EvalCardLabScreen } from '@/visual-lab/chapters/eval-card/EvalCardLabScreen';
import { GradeReportLabScreen } from '@/visual-lab/chapters/grade-report/GradeReportLabScreen';
import {
  ORAL_EXAM_TASK_LAB_PATH,
  OralExamTaskLabScreen,
} from '@/visual-lab/chapters/oral-exam-task/OralExamTaskLabScreen';
import { ToastLabScreen } from '@/visual-lab/chapters/toast/ToastLabScreen';
import {
  UPGRADE_OFFER_LAB_PATH,
  UpgradeOfferLabScreen,
} from '@/visual-lab/chapters/upgrade-offer/UpgradeOfferLabScreen';
import {
  ANALYTICS_LAB_PATH,
  AnalyticsLabScreen,
} from '@/visual-lab/chapters/analytics/AnalyticsLabScreen';
import { LabNavLink } from '@/visual-lab/LabNavLink';
import { VisualLabIndex } from '@/visual-lab/VisualLabIndex';

const ORB_LAB_REDIRECT = '/visual-lab/orb';
const TOAST_LAB_PATH = '/visual-lab/toast';
const EVAL_CARD_LAB_PATH = '/visual-lab/eval-card';
const ORAL_EXAM_LAB_PATH = '/visual-lab/oral-exam';
const GRADE_REPORT_LAB_PATH = '/visual-lab/grade-report';
const PATROL_LAB_ALIAS = '/visual-lab/patrol';

function normalizePathname(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1);
  }
  return pathname;
}

function isOrbLabLegacyPath(pathname: string): boolean {
  const normalized = normalizePathname(pathname);
  return normalized === '/orb-lab' || normalized.startsWith('/orb-lab/');
}

function isVisualLabIndex(pathname: string): boolean {
  const normalized = normalizePathname(pathname);
  return normalized === '/visual-lab';
}

function canonicalizePathname(pathname: string): string {
  if (isOrbLabLegacyPath(pathname)) {
    return ORB_LAB_REDIRECT;
  }
  if (normalizePathname(pathname) === PATROL_LAB_ALIAS) {
    return ORAL_EXAM_LAB_PATH;
  }
  return pathname;
}

function isOrbChapter(pathname: string): boolean {
  return canonicalizePathname(pathname) === ORB_LAB_REDIRECT;
}

function isToastChapter(pathname: string): boolean {
  return normalizePathname(pathname) === TOAST_LAB_PATH;
}

function isEvalCardChapter(pathname: string): boolean {
  return normalizePathname(pathname) === EVAL_CARD_LAB_PATH;
}

function isOralExamChapter(pathname: string): boolean {
  const normalized = normalizePathname(pathname);
  return normalized === ORAL_EXAM_LAB_PATH || normalized === PATROL_LAB_ALIAS;
}

function isOralExamTaskChapter(pathname: string): boolean {
  return normalizePathname(pathname) === ORAL_EXAM_TASK_LAB_PATH;
}

function isGradeReportChapter(pathname: string): boolean {
  return normalizePathname(pathname) === GRADE_REPORT_LAB_PATH;
}

function isUpgradeOfferChapter(pathname: string): boolean {
  return normalizePathname(pathname) === UPGRADE_OFFER_LAB_PATH;
}

function isAnalyticsChapter(pathname: string): boolean {
  return normalizePathname(pathname) === ANALYTICS_LAB_PATH;
}

function readPathname(): string {
  if (typeof window === 'undefined') {
    return '/visual-lab';
  }
  return window.location.pathname;
}

function readSearch(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  return window.location.search ?? '';
}

function splitHref(path: string): { pathname: string; search: string } {
  const q = path.indexOf('?');
  if (q === -1) {
    return { pathname: path, search: '' };
  }
  return { pathname: path.slice(0, q), search: `?${path.slice(q + 1)}` };
}

/**
 * Visual Lab shell — compact chrome, client-side path switch, and legacy /orb-lab redirect.
 */
export function VisualLabApp() {
  const [pathname, setPathname] = useState(() => canonicalizePathname(readPathname()));
  const [search, setSearch] = useState(() => readSearch());

  useEffect(() => {
    const currentPath = readPathname();
    if (isOrbLabLegacyPath(currentPath)) {
      window.history.replaceState(null, '', ORB_LAB_REDIRECT);
      setPathname(ORB_LAB_REDIRECT);
      setSearch('');
      return undefined;
    }

    if (normalizePathname(currentPath) === PATROL_LAB_ALIAS) {
      const next = `${ORAL_EXAM_LAB_PATH}${readSearch()}`;
      window.history.replaceState(null, '', next);
      setPathname(ORAL_EXAM_LAB_PATH);
      return undefined;
    }

    if (typeof window.addEventListener !== 'function') {
      return undefined;
    }

    const syncLocation = () => {
      setPathname(readPathname());
      setSearch(readSearch());
    };
    window.addEventListener('popstate', syncLocation);
    return () => window.removeEventListener('popstate', syncLocation);
  }, []);

  const navigate = (path: string) => {
    if (typeof window.history?.pushState === 'function') {
      window.history.pushState(null, '', path);
    }
    const next = splitHref(path);
    setPathname(next.pathname);
    setSearch(next.search);
  };

  const showIndex = isVisualLabIndex(pathname);
  const showOrb = isOrbChapter(pathname);
  const showToast = isToastChapter(pathname);
  const showEvalCard = isEvalCardChapter(pathname);
  const showOralExam = isOralExamChapter(pathname);
  const showGradeReport = isGradeReportChapter(pathname);
  const showOralExamTask = isOralExamTaskChapter(pathname);
  const showUpgradeOffer = isUpgradeOfferChapter(pathname);
  const showAnalytics = isAnalyticsChapter(pathname);
  const patrolQuery = parsePatrolLabQuery(search);

  return (
    <Box flex={1} bg="$bgApp" width="100%">
      <Box
        borderBottomWidth={1}
        borderColor="$borderLight"
        bg="$bgCard"
        px="$3"
        py="$1"
        minHeight={36}
        justifyContent="center"
      >
        <HStack alignItems="center" space="md" flexWrap="nowrap">
          <LabNavLink
            accessibilityRole="link"
            href="/visual-lab"
            onPress={() => navigate('/visual-lab')}
          >
            <Text size="xs" color="$textHeading" fontWeight="semibold">Home</Text>
          </LabNavLink>
          <HStack space="sm">
            <LabNavLink
              accessibilityRole="link"
              href={ORB_LAB_REDIRECT}
              onPress={() => navigate(ORB_LAB_REDIRECT)}
            >
              <Text
                size="xs"
                color={showOrb ? '$textHeading' : '$textSubtle'}
                fontWeight={showOrb ? 'semibold' : 'normal'}
              >
                Orb
              </Text>
            </LabNavLink>
            <LabNavLink
              accessibilityRole="link"
              href={TOAST_LAB_PATH}
              onPress={() => navigate(TOAST_LAB_PATH)}
            >
              <Text
                size="xs"
                color={showToast ? '$textHeading' : '$textSubtle'}
                fontWeight={showToast ? 'semibold' : 'normal'}
              >
                Toast
              </Text>
            </LabNavLink>
            <LabNavLink
              accessibilityRole="link"
              href={EVAL_CARD_LAB_PATH}
              onPress={() => navigate(
                showEvalCard
                  ? `${EVAL_CARD_LAB_PATH}${search || '?mark=both&levelLine=on'}`
                  : `${EVAL_CARD_LAB_PATH}?mark=both&levelLine=on`,
              )}
            >
              <Text
                size="xs"
                color={showEvalCard ? '$textHeading' : '$textSubtle'}
                fontWeight={showEvalCard ? 'semibold' : 'normal'}
              >
                Eval Card
              </Text>
            </LabNavLink>
            <LabNavLink
              accessibilityRole="link"
              href={ORAL_EXAM_LAB_PATH}
              onPress={() => navigate(showOralExam
                ? `${ORAL_EXAM_LAB_PATH}${search || '?stage=observe&actor=supervisor'}`
                : `${ORAL_EXAM_LAB_PATH}?stage=observe&actor=supervisor`)}
            >
              <Text
                size="xs"
                color={showOralExam ? '$textHeading' : '$textSubtle'}
                fontWeight={showOralExam ? 'semibold' : 'normal'}
              >
                Oral Exam
              </Text>
            </LabNavLink>
            <LabNavLink
              accessibilityRole="link"
              href={ORAL_EXAM_TASK_LAB_PATH}
              onPress={() => navigate(ORAL_EXAM_TASK_LAB_PATH)}
            >
              <Text
                size="xs"
                color={showOralExamTask ? '$textHeading' : '$textSubtle'}
                fontWeight={showOralExamTask ? 'semibold' : 'normal'}
              >
                Oral exam task
              </Text>
            </LabNavLink>
            <LabNavLink
              accessibilityRole="link"
              href={GRADE_REPORT_LAB_PATH}
              onPress={() => navigate(GRADE_REPORT_LAB_PATH)}
            >
              <Text
                size="xs"
                color={showGradeReport ? '$textHeading' : '$textSubtle'}
                fontWeight={showGradeReport ? 'semibold' : 'normal'}
              >
                Grade report
              </Text>
            </LabNavLink>
            <LabNavLink
              accessibilityRole="link"
              href={UPGRADE_OFFER_LAB_PATH}
              onPress={() => navigate(UPGRADE_OFFER_LAB_PATH)}
            >
              <Text
                size="xs"
                color={showUpgradeOffer ? '$textHeading' : '$textSubtle'}
                fontWeight={showUpgradeOffer ? 'semibold' : 'normal'}
              >
                Upgrade offer
              </Text>
            </LabNavLink>
            <LabNavLink
              accessibilityRole="link"
              href={ANALYTICS_LAB_PATH}
              onPress={() => navigate(ANALYTICS_LAB_PATH)}
            >
              <Text
                size="xs"
                color={showAnalytics ? '$textHeading' : '$textSubtle'}
                fontWeight={showAnalytics ? 'semibold' : 'normal'}
              >
                Analytics
              </Text>
            </LabNavLink>
          </HStack>
          {showOralExam ? (
            <PatrolLabHeaderControls query={patrolQuery} onNavigate={navigate} />
          ) : null}
        </HStack>
      </Box>

      <Box flex={1} minHeight={0}>
        {showIndex && <VisualLabIndex onNavigate={navigate} />}
        {showOrb && <OrbLabScreen />}
        {showToast && <ToastLabScreen />}
        {showEvalCard && <EvalCardLabScreen search={search} onNavigate={navigate} />}
        {showOralExam && <PatrolLabScreen search={search} />}
        {showGradeReport && <GradeReportLabScreen />}
        {showOralExamTask && <OralExamTaskLabScreen search={search} onNavigate={navigate} />}
        {showUpgradeOffer && <UpgradeOfferLabScreen search={search} onNavigate={navigate} />}
        {showAnalytics && <AnalyticsLabScreen search={search} />}
        {!showIndex && !showOrb && !showToast && !showEvalCard && !showOralExam && !showGradeReport
          && !showOralExamTask && !showUpgradeOffer && !showAnalytics && (
          <VisualLabIndex onNavigate={navigate} />
        )}
      </Box>
    </Box>
  );
}
